import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import {
  Home, PlusCircle, FolderCode, LayoutTemplate, Boxes, Rocket,
  Settings, User, Shield, LogOut,
} from "lucide-react";

const NAV = [
  { name: "Home", icon: Home, to: "/dashboard", end: true },
  { name: "New Project", icon: PlusCircle, to: "/dashboard/new" },
  { name: "Projects", icon: FolderCode, to: "/dashboard/projects" },
  { name: "Templates", icon: LayoutTemplate, to: "/dashboard/templates" },
  { name: "Integrations", icon: Boxes, to: "/dashboard/integrations" },
  { name: "Deployments", icon: Rocket, to: "/dashboard/deployments" },
  { name: "Settings", icon: Settings, to: "/dashboard/settings" },
  { name: "Account", icon: User, to: "/dashboard/account" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100 flex">
      <aside className="w-60 shrink-0 border-r border-slate-800/70 bg-slate-950/40 flex flex-col fixed h-screen">
        <div className="h-16 flex items-center px-5 border-b border-slate-800/70"><Logo /></div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto dc-scroll">
          {NAV.map((n) => (
            <NavLink key={n.name} to={n.to} end={n.end} data-testid={`nav-${n.name.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-emerald-500/15 text-emerald-300 font-medium" : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                }`}>
              <n.icon className="h-4 w-4" /> {n.name}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-amber-300/90 hover:bg-amber-500/10 transition-colors">
              <Shield className="h-4 w-4" /> Admin Panel
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-slate-800/70">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-slate-950 text-xs font-bold overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "U").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }}
              className="text-slate-500 hover:text-rose-400 transition-colors"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </aside>

      <div className="flex-1 ml-60">
        <div className="h-16 border-b border-slate-800/70 dc-glass sticky top-0 z-30 flex items-center justify-end px-6 gap-3">
          <ThemeToggle />
        </div>
        <main className="p-6 max-w-7xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
